import React, { memo, ReactNode } from "react";
import styled, { useTheme } from "~/ui/styled";
import { Flex } from "~components/El";

interface Props {
  label?: string | ReactNode;
  value: string;
  placeholder?: string;
  editable?: boolean;
  required?: boolean;
  maxLength?: number;
  keyboardType?: string;
  decimal?: number;
  prefix?: ReactNode;
  suffix?: ReactNode;
  error?: string | boolean;
  children?: ReactNode;
  onChangeText?: (t: string) => void;
  onBlur?: () => void;
}

const InlineInput: React.FC<Props> = ({
  label,
  value = "",
  placeholder = "请输入",
  editable = true,
  required = false,
  maxLength = 20,
  keyboardType = "default",
  decimal = 2,
  prefix,
  suffix,
  error = "",
  children = null,
  onChangeText = () => {},
  onBlur = () => {},
}) => {
  if (children) return <>{children}</>;
  const theme = useTheme();

  const handleChange = (v: string) => {
    let filtered = v;

    switch (keyboardType) {
      case "numeric":
        filtered = v.replace(/[^0-9]/g, "");
        break;

      case "number-pad":
        filtered = v.replace(/[^0-9-]/g, "");
        filtered = filtered.replace(/(?!^)-/g, "");
        break;

      case "decimal-pad":
        filtered = v.replace(/[^0-9.]/g, "");
        const parts = filtered.split(".");
        if (parts.length > 2) filtered = parts[0] + "." + parts[1];
        if (parts[1]?.length > decimal) {
          filtered = parts[0] + "." + parts[1].slice(0, decimal);
        }
        break;

      default:
        break;
    }

    onChangeText(filtered);
  };

  return (
    <Container>
      {label && (
        <Label>
          {typeof label === "string" ? <LabelText>{required && <RequiredText>*</RequiredText>}{label}</LabelText> : label}
        </Label>
      )}

      <InputRow error={!!error}>
        {prefix && <Prefix>{prefix}</Prefix>}

        <Input
          value={value}
          editable={editable}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.inputPlaceholderColor}
          keyboardType={keyboardType}
          maxLength={maxLength}
          underlineColorAndroid="transparent"
          onChangeText={handleChange}
          onBlur={onBlur}
        />

        {suffix && <Suffix>{suffix}</Suffix>}
      </InputRow>

      {error ? <ErrorText>{error}</ErrorText> : null}
    </Container>
  );
};

export default memo(InlineInput);

//
// ---------------- styled ----------------
//

const Container = styled(Flex)`
  flex-direction: column;
  flex: 1;
`;

const Label = styled.View`
  padding-bottom: 6px;
`;

const LabelText = styled.Text`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const RequiredText = styled.Text`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
  margin-right: 5px;
`;

const InputRow = styled(Flex)<{ error: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 10px 0;
`;

const Input = styled.TextInput`
  flex: 1;
  height: 100%;
  padding: 8px 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const Prefix = styled.View`
  flex-direction: row;
  align-items: center;
  padding-right: 6px;
`;

const Suffix = styled.View`
  flex-direction: row;
  align-items: center;
`;


const ErrorText = styled.Text`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error};
`;
